import React, { useState, useEffect } from "react";
import config from '../config/config';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  IconButton,
} from "@mui/material";

const S = {
  white: '#ffffff',
  bg: '#f9fafb',
  border: '#e5e7eb',
  accent: '#111827',
  sub: '#6b7280',
  muted: '#9ca3af',
  error: '#ef4444',
  errorBg: '#fef2f2',
  success: '#10b981',
  successBg: '#ecfdf5',
  radius: '16px',
  radiusSm: '10px',
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.06)',
  font: "'DM Sans', 'Helvetica Neue', sans-serif",
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: S.radiusSm,
    fontFamily: S.font,
    fontSize: '13.5px',
    fontWeight: 500,
    background: S.white,
    '& fieldset': { borderColor: S.border },
    '&:hover fieldset': { borderColor: '#9ca3af' },
    '&.Mui-focused fieldset': { borderColor: S.accent, borderWidth: '1.5px' },
    '&.Mui-disabled': { background: S.bg },
  },
  '& .MuiInputLabel-root': {
    fontFamily: S.font,
    fontSize: '13px',
    fontWeight: 500,
    color: S.sub,
  },
  '& .MuiInputLabel-root.Mui-focused': { color: S.accent },
};

const PaymentModal = ({ open, onClose, invoice, invoiceItems, accounts, onSubmit }) => {
  const [transactionCode, setTransactionCode] = useState("");
  const [excessAmounts, setExcessAmounts] = useState({});
  const [paymentInputs, setPaymentInputs] = useState({});
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const userInfor = localStorage.getItem('userInfor') || '';
  const isSuperAdmin = userInfor === 'Super Admin';

  useEffect(() => {
    if (open && invoiceItems?.length) {
      const initialInputs = {};
      const initialExcess = {};
      invoiceItems.forEach((item) => {
        initialInputs[item.id] = "0.00";
        initialExcess[item.id] = 0;
      });
      setPaymentInputs(initialInputs);
      setExcessAmounts(initialExcess);
      setTransactionCode("");
      setPaymentMode("");
      setPaymentNote("");
      setErrors({});
      setMessage(null);
      setLoading(false);
    }
  }, [open, invoiceItems]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KSH' }).format(amount);
  };

  const validateInputs = () => {
    const newErrors = {};
    let hasError = false;

    if (!transactionCode.trim()) {
      newErrors.transactionCode = "Transaction code is required";
      hasError = true;
    }

    if (!paymentMode) {
      newErrors.paymentMode = "Please select a payment mode";
      hasError = true;
    }

    if (!message) {
      newErrors.message = "Payment note is required.";
      hasError = true;
    }

    invoiceItems.forEach((item) => {
      const val = paymentInputs[item.id];
      const balance = parseFloat(item.total) - parseFloat(item.paid || 0);

      if (val === "" || val === undefined) {
        newErrors[item.id] = "Amount is required";
        hasError = true;
      } else if (isNaN(val)) {
        newErrors[item.id] = "Amount must be a number";
        hasError = true;
      } else if (parseFloat(val) < 0) {
        newErrors[item.id] = "Amount cannot be negative";
        hasError = true;
      } else if (parseFloat(val) > balance) {
        newErrors[item.id] = "Amount cannot be more than what is expected";
        hasError = true;
      }
    });

    setErrors(newErrors);
    return !hasError;
  };

  const handleInputChange = (itemId, maxAmount, value) => {
    if (value === "") {
      setPaymentInputs((prev) => ({ ...prev, [itemId]: "" }));
      setExcessAmounts((prev) => ({ ...prev, [itemId]: 0 }));
      setErrors((prev) => ({ ...prev, [itemId]: null }));
      return;
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      setErrors((prev) => ({ ...prev, [itemId]: "Invalid amount" }));
      return;
    }

    const amount = parseFloat(value);
    if (isNaN(amount) || amount < 0) {
      setErrors((prev) => ({ ...prev, [itemId]: "Invalid amount" }));
      return;
    }

    const excess = amount > maxAmount ? (amount - maxAmount).toFixed(2) : 0;
    setExcessAmounts((prev) => ({ ...prev, [itemId]: excess }));
    setPaymentInputs((prev) => ({ ...prev, [itemId]: value }));
    setErrors((prev) => ({ ...prev, [itemId]: null }));
  };

  const handleSave = async () => {
    setMessage(null);
    if (!validateInputs()) {
      setMessage({ type: "error", text: "Please fix validation errors." });
      return;
    }

    const hasExcess = Object.values(excessAmounts).some((excess) => parseFloat(excess) > 0);
    if (hasExcess) {
      const proceed = window.confirm(
        "Some items have excess payments. Please review before saving. Do you want to continue?"
      );
      if (!proceed) return;
    }

    const payload = {
      invoice_id: invoice,
      transaction_code: transactionCode,
      payment_mode: paymentMode,
      payment_note: paymentNote,
      payments: Object.entries(paymentInputs).map(([itemId, amount]) => ({
        item_id: itemId,
        amount_paid: parseFloat(amount),
      })),
    };

    try {
      setLoading(true);
      const response = await onSubmit(payload);
      if (response.success) {
        setMessage({ type: "success", text: response.message || "Payment saved successfully." });
      } else {
        setMessage({ type: "error", text: response.message || "Failed to save payment." });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "An error occurred." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: S.radius,
          boxShadow: S.shadow,
          background: S.white,
          border: `1px solid ${S.border}`,
          fontFamily: S.font,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3.5,
          pt: 3,
          pb: 2.5,
          borderBottom: `1px solid ${S.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{ fontFamily: S.font, fontWeight: 700, fontSize: '17px', color: S.accent, letterSpacing: '-0.02em' }}
          >
            Add Payment
          </Typography>
          <Typography sx={{ fontFamily: S.font, fontSize: '12px', color: S.muted, mt: 0.3 }}>
            Expense #{invoice?.invoiceNumber || invoice?.invoice_number || invoice}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            border: `1px solid ${S.border}`,
            color: S.sub,
            '&:hover': { background: S.bg },
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3.5, py: 3 }}>
        <br/> <br/>
        {message && (
          <Box
            sx={{
              mb: 2.5,
              px: 2,
              py: 1.5,
              borderRadius: S.radiusSm,
              background: message.type === 'success' ? S.successBg : S.errorBg,
              border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: message.type === 'success' ? S.success : S.error,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontFamily: S.font,
                fontSize: '13px',
                fontWeight: 500,
                color: message.type === 'success' ? S.success : S.error,
              }}
            >
              {message.text}
            </Typography>
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Transaction Code"
              value={transactionCode}
              onChange={(e) => setTransactionCode(e.target.value)}
              disabled={loading}
              fullWidth
              error={Boolean(errors.transactionCode)}
              helperText={errors.transactionCode}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={Boolean(errors.paymentMode)} sx={fieldSx}>
              <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
              <Select
                labelId="payment-mode-label"
                value={paymentMode}
                label="Payment Mode"
                onChange={(e) => setPaymentMode(e.target.value)}
                disabled={loading}
                sx={{ fontFamily: S.font, fontSize: '13.5px', fontWeight: 500 }}
              >
                {accounts.length > 0 && accounts.map((account) => (
                  <MenuItem
                    key={account.type}
                    value={account.type}
                    sx={{ fontFamily: S.font, fontSize: '13px', fontWeight: 500 }}
                  >
                    {account.name}
                    <Typography
                      component="span"
                      sx={{ ml: 1, fontSize: '11.5px', color: S.muted, fontFamily: S.font }}
                    >
                      ({formatCurrency(account.balance)})
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              {errors.paymentMode && (
                <Typography sx={{ color: S.error, fontSize: '11.5px', mt: 0.5, ml: 1.5, fontFamily: S.font }}>
                  {errors.paymentMode}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Typography
          sx={{
            fontFamily: S.font,
            fontSize: '11px',
            fontWeight: 700,
            color: S.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1.5,
          }}
        >
          Items
        </Typography>

        {invoiceItems?.length ? (
          invoiceItems.map((item) => {
            const balance = parseFloat(item.total) - parseFloat(item.paid || 0);
            return (
              <Box
                key={item.id}
                sx={{
                  background: S.bg,
                  borderRadius: S.radiusSm,
                  border: `1px solid ${S.border}`,
                  px: 2.5,
                  py: 2,
                  mb: 1.5,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <Typography
                      sx={{ fontFamily: S.font, fontSize: '13.5px', fontWeight: 600, color: S.accent }}
                    >
                      {item.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.4 }}>
                      <Typography sx={{ fontFamily: S.font, fontSize: '12px', color: S.muted }}>
                        Balance:
                      </Typography>
                      <Typography sx={{ fontFamily: S.font, fontSize: '12px', fontWeight: 600, color: S.sub }}>
                        KES {balance.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Amount Paid"
                      fullWidth
                      type="tel"
                      inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                      value={paymentInputs[item.id] ?? ""}
                      placeholder={balance.toFixed(2)}
                      onChange={(e) => handleInputChange(item.id, balance, e.target.value)}
                      error={Boolean(errors[item.id])}
                      helperText={errors[item.id]}
                      disabled={loading}
                      sx={fieldSx}
                    />
                    {excessAmounts[item.id] > 0 && (
                      <Typography
                        sx={{ fontFamily: S.font, fontSize: '11.5px', color: S.error, mt: 0.5, ml: 0.5 }}
                      >
                        Excess: KES {excessAmounts[item.id]} — invalid
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            );
          })
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              borderRadius: S.radiusSm,
              border: `1px dashed ${S.border}`,
              background: S.bg,
            }}
          >
            <Typography sx={{ fontFamily: S.font, fontSize: '13.5px', color: S.muted }}>
              No expense items found.
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 2.5 }}>
          <TextField
            label="Payment Note"
            multiline
            rows={3}
            fullWidth
            value={paymentNote}
            onChange={(e) => setPaymentNote(e.target.value)}
            disabled={loading}
            error={Boolean(errors.message)}
            sx={fieldSx}
          />
          {errors.message && (
            <Typography sx={{ color: S.error, fontSize: '11.5px', mt: 0.5, ml: 1.5, fontFamily: S.font }}>
              {errors.message}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3.5,
          pb: 3.5,
          pt: 2,
          borderTop: `1px solid ${S.border}`,
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: S.font,
            fontWeight: 600,
            fontSize: '13.5px',
            color: S.sub,
            background: S.bg,
            border: `1px solid ${S.border}`,
            borderRadius: '9px',
            textTransform: 'none',
            px: 3,
            py: 1.1,
            '&:hover': { background: S.border },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          sx={{
            fontFamily: S.font,
            fontWeight: 600,
            fontSize: '13.5px',
            color: S.white,
            background: S.accent,
            borderRadius: '9px',
            textTransform: 'none',
            px: 3,
            py: 1.1,
            minWidth: 140,
            '&:hover': { background: '#374151' },
            '&.Mui-disabled': { background: '#d1d5db', color: S.white },
          }}
        >
          {loading ? "Saving..." : "Save Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;